import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/components/SmoothScroll";

const LINKS = [
  { label: "The Archive", to: "/", anchor: "discography" },
  { label: "About", to: "/credits" },
  { label: "Merch", to: "/merch" },
  { label: "Artifacts", to: "/nft" },
  { label: "Contact", to: "/contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, link) => {
    if (link.anchor && location.pathname === "/") {
      e.preventDefault();
      scrollToId(link.anchor);
      setOpen(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-6 md:px-12 py-5">
          <Link
            to="/"
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0 })}
            className="font-machine text-xl tracking-[0.4em] text-[#f2f0ec] font-medium select-none"
          >
            ØČËĀŃ
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={(e) => handleClick(e, l)}
                className={`font-mono text-[11px] uppercase tracking-[0.25em] link-underline transition-colors ${
                  location.pathname === l.to ? "text-[#f2f0ec]" : "text-[#8a8a8a] hover:text-[#f2f0ec]"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <button
            data-testid="nav-menu-toggle"
            className="md:hidden text-[#f2f0ec]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8 md:hidden"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.label}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Link
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={(e) => handleClick(e, l)}
                  className="font-serif text-4xl text-[#f2f0ec]"
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
